import isNil from 'lodash/isNil'
import React, { useState } from 'react'
import {
  Bar,
  BarChart,
  Cell,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { CategoricalChartFunc } from 'recharts/types/chart/generateCategoricalChart'
import { Props } from 'recharts/types/component/ResponsiveContainer'
import { AnyToken } from 'src/common/types/models/Tokens'
import { displayAmount } from 'src/common/utils/formatters/number'
import theme from 'src/theme'
import MarketDepthChartSkeleton from './components/MarketDepthChartSkeleton'
import MarketDepthChartTooltip from './components/MarketDepthChartTooltip'
import useMarketDepthData from './useMarketDepthData'
import { MD_DECIMALS } from './utils'
import { BigSource } from 'big.js'

interface ChartProps
  extends Omit<Props, 'children'>,
    React.RefAttributes<
      | HTMLDivElement
      | {
          current: HTMLDivElement
        }
    > {
  sellToken: AnyToken
  buyToken: AnyToken
  onAmountSelect?(value: number): void
  defaultPrice?: number
}

const _displayAmount = (amount?: BigSource) =>
  amount && displayAmount(amount, { maxDecimals: 2, minThreshold: 0.01 })

const MarketDepthChart: React.FC<ChartProps> = ({
  sellToken,
  buyToken,
  onAmountSelect,
  defaultPrice,
  ...props
}) => {
  const { data, marketPrice, loading } = useMarketDepthData(sellToken, buyToken)

  // Data is already sorted by price.
  // Map each data point to add a sequential id (0, 1, 2, …)
  const indexedData = data.map((item, index) => ({ ...item, id: index }))

  // We still use the raw price values for computing markers.
  const minPriceValue = data[0]?.price

  // Maximum offers amount for YAxis domain.
  const maxOffersAmount = Math.max(
    ...data.map(d => d.blockOffers + d.partialOffers)
  )

  // Set the initial selected price (or default to minPrice)
  const [selectedPrice, setSelectedPrice] = useState<number>(
    defaultPrice || minPriceValue
  )

  const [hoveredPrice, setHoveredPrice] = useState<number>(
    defaultPrice || minPriceValue
  )

  if (!data || data.length === 0) return null

  // When a bar is clicked, update the selected price.
  const handleBarClick: CategoricalChartFunc = state => {
    if (state?.activePayload && state.activePayload.length) {
      // Use the sequential id from the clicked bar
      const clickedId = state.activePayload[0].payload.id
      const clickedPrice = data[clickedId].price
      if (clickedPrice > 0) {
        const clickedAmount = [...data]
          .splice(0, clickedId + 1)
          .reduce((sum, item) => sum + item.blockOffers + item.partialOffers, 0)
        setSelectedPrice(clickedPrice)
        onAmountSelect && onAmountSelect(clickedAmount)
      }
    }
  }

  const handleBarHover: CategoricalChartFunc = state => {
    if (state?.activePayload && state.activePayload.length) {
      // Use the sequential id from the hovered bar
      const hoveredId = state.activePayload[0].payload.id
      const hoveredPrice = data[hoveredId].price
      setHoveredPrice(hoveredPrice)
    }
  }

  const isNotSelected = (price: number) => {
    return price === 0 || (price > selectedPrice && price > hoveredPrice)
  }

  const getCellColor = (price: number) => {
    if (price === 0 || (price > selectedPrice && price > hoveredPrice)) {
      return theme.colors.chart.muted
    }

    return theme.colors.chart.accent
  }

  const getMinPointSizePartial = (value: number, index: number) => {
    const _value = value - data[index].blockOffers
    return _value > 0 ? 7 : 3
  }
  const getMinPointSizeFull = (value: number, index: number) => {
    const _value = value - data[index].partialOffers
    return _value > 0 ? 7 : 0
  }

  // Determine the index of the selected data point.
  const startIndex = data.findIndex(d => d.price > 0)

  const selectedIndex = data.findIndex(d => d.price === selectedPrice)
  const hoveredIndex = data.findIndex(d => d.price === hoveredPrice)
  const marketIndex = marketPrice
    ? data.findIndex(d => d.price === Number(marketPrice.toFixed(MD_DECIMALS)))
    : null

  const barWidth = 8

  if (loading)
    return (
      <div className='paper w-full p-2.5'>
        <MarketDepthChartSkeleton />
      </div>
    )

  return (
    <div className='paper w-full p-2.5'>
      <ResponsiveContainer width='100%' height={200} {...props}>
        <BarChart
          data={indexedData}
          onClick={handleBarClick}
          onMouseMove={handleBarHover}
          margin={{ top: 30, bottom: 30, left: 30, right: 30 }}
          // Use these to remove any extra gaps between bars:
          barGap={2} // a small gap between bars
          barCategoryGap='0%' // no extra gap for missing categories
        >
          {/* Use the sequential id as the x-axis */}
          <XAxis
            dataKey='id'
            type='category'
            hide
            tickFormatter={tick => {
              // tick is the sequential id (0, 1, 2, …)
              // Use it to look up the corresponding data point and display its price.
              const d = data[tick]
              return d ? d.price.toFixed(MD_DECIMALS) : tick
            }}
          />
          <YAxis hide domain={[0, maxOffersAmount]} />

          {/* ----- MIN PRICE REFERENCE (First bar) ----- */}
          {/* Vertical line for the first bar (id 0) */}
          {/* <ReferenceLine
          x={0}
          stroke={theme.colors.chart.DEFAULT}
          strokeWidth={2}
        /> */}
          {/* <ReferenceLine
          x={0}
          stroke='none'
          ifOverflow='extendDomain'
          label={{
            value: 'Min',
            position: 'top',
            fontSize: 12,
            fill: theme.colors.chart.text,
            dy: -5,
          }}
        />
        <ReferenceLine
          x={0}
          stroke='none'
          ifOverflow='extendDomain'
          label={{
            value: minValue,
            position: 'bottom',
            fontSize: 12,
            fill: theme.colors.chart.text,
            dy: 5,
          }}
        /> */}

          {/* ----- MARKET PRICE REFERENCE ----- */}
          {!isNil(marketIndex) && marketPrice && (
            <>
              <ReferenceLine
                x={marketIndex}
                stroke={theme.colors.chart.DEFAULT}
                strokeDasharray='5 5'
                strokeWidth={2}
                isFront
              />
              <ReferenceLine
                x={marketIndex}
                stroke='none'
                ifOverflow='extendDomain'
                label={{
                  value: 'Market',
                  position: 'top',
                  fill: theme.colors.chart.text,
                  fontSize: 12,
                  dy: -5,
                }}
              />
              <ReferenceLine
                x={marketIndex}
                stroke='none'
                ifOverflow='extendDomain'
                label={{
                  value: _displayAmount(marketPrice),
                  position: 'bottom',
                  fill: theme.colors.chart.text,
                  fontSize: 12,
                  dy: 5,
                }}
              />
            </>
          )}

          {/* ----- SELECTED / PRICE REFERENCE ----- */}
          {selectedIndex && (
            <ReferenceLine
              x={selectedIndex}
              stroke={theme.colors.chart.DEFAULT}
              strokeWidth={2}
              isFront
            />
          )}

          <Tooltip
            content={
              <MarketDepthChartTooltip
                data={data}
                sellToken={sellToken}
                buyToken={buyToken}
              />
            }
            cursor={{
              fill: 'rgba(0, 0, 0, 0.1)', // semi-transparent fill
              width: barWidth,
            }}
          />

          {/* Optional: highlight the selected area between the first and selected bars */}
          {(selectedIndex || hoveredIndex) && (
            <ReferenceArea
              x1={startIndex}
              x2={hoveredIndex || selectedIndex}
              y1={0}
              y2={maxOffersAmount}
              fill={theme.colors.chart.DEFAULT}
              isFront
              fillOpacity={0.2}
            />
          )}

          {/* Add minPointSize to guarantee a minimum bar height */}
          {/* Render bars with conditional color */}
          <Bar
            dataKey='partialOffers'
            stackId='a'
            barSize={barWidth} // Because of strokeWidth we need to adjust the bar size by strokeWidth
            minPointSize={getMinPointSizePartial}
          >
            {indexedData.map((entry, index) => (
              <Cell
                key={`partial-${index}`}
                fill={getCellColor(entry.price)}
                fillOpacity={isNotSelected(entry.price) ? 1 : 0.6}
              />
            ))}
          </Bar>

          {/* Because of strokeWidth we need to adjust the bar size by strokeWidth  */}
          <Bar
            dataKey='blockOffers'
            stackId='a'
            barSize={barWidth}
            minPointSize={getMinPointSizeFull}
          >
            {indexedData.map((entry, index) => (
              <Cell
                key={`full-${index}`}
                fill={getCellColor(entry.price)}
                fillOpacity={isNotSelected(entry.price) ? 1 : 0.3}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default MarketDepthChart
